// Central fetch wrapper: same-origin cookies + CSRF header on mutations.
const BASE = import.meta.env.VITE_API_BASE || '/api';

let csrfToken = null;
export function setCsrf(token) { csrfToken = token; }
export function getCsrf() { return csrfToken; }

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function request(method, path, { body, isForm } = {}) {
  const headers = {};
  const opts = { method, credentials: 'include', headers };

  if (csrfToken && method !== 'GET') {
    headers['X-CSRF-Token'] = csrfToken;
  }
  if (body !== undefined) {
    if (isForm) {
      opts.body = body; // FormData — let the browser set Content-Type
    } else {
      headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
  }

  // Automatically retry idempotent GETs on transient failures (network blip
  // or a 5xx). Mutations are never retried — a retry could double-apply them.
  const maxAttempts = method === 'GET' ? 3 : 1;
  let lastErr = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let res;
    try {
      res = await fetch(`${BASE}${path}`, opts);
    } catch (networkErr) {
      // fetch rejects only on network/connectivity errors.
      lastErr = Object.assign(new Error('Network error — please check your connection.'), { status: 0, fields: null });
      if (attempt < maxAttempts) { await sleep(300 * attempt); continue; }
      throw lastErr;
    }

    const text = await res.text();
    let data = null;
    try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }

    if (!res.ok) {
      const err = new Error(data?.error || `Request failed (${res.status})`);
      err.status = res.status;
      err.fields = data?.fields || null;
      if (res.status >= 500 && attempt < maxAttempts) { lastErr = err; await sleep(300 * attempt); continue; }
      throw err;
    }
    return data;
  }
  throw lastErr;
}

export const api = {
  get: (p) => request('GET', p),
  post: (p, body) => request('POST', p, { body }),
  put: (p, body) => request('PUT', p, { body }),
  patch: (p, body) => request('PATCH', p, { body }),
  del: (p) => request('DELETE', p),
  upload: (p, formData) => request('POST', p, { body: formData, isForm: true }),
};
